import { useMemo, useState } from 'react'

import { EmptyState, ErrorState, SkeletonCards } from '@/shared/components/data'
import { Segmented } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { usePortalBoard, usePortalProjects } from './api'
import { PhaseBadge } from './components/PhaseBadge'
import { PortalProjectCard } from './components/PortalProjectCard'
import type { PortalProjectListItem } from './types'

type View = 'todos' | 'fase'

/**
 * `/portal` — lo que ve el cliente al entrar.
 *
 * Tarjetas grandes, no una tabla densa: esta pantalla se abre una vez por
 * semana, no ocho horas por día. La agrupación por fase usa `GET /portal/board`,
 * que arma columnas fijas del lado del server: si el equipo agrega una columna
 * interna ("Esperando factura"), acá no cambia nada.
 *
 * Todo lo de esta pantalla es de solo lectura. No hay un botón que escriba, y
 * tampoco un hook que pudiera hacerlo (ver `client.ts`).
 */
export function PortalProjectsPage() {
  const t = useT()
  const [view, setView] = useState<View>('todos')

  // El `value` es una constante del código; el `label` sale del catálogo y
  // cambia con el idioma, así que la tabla se arma adentro del componente.
  const views = useMemo(
    () =>
      [
        { value: 'todos', label: t('portal.projects.viewAll') },
        { value: 'fase', label: t('portal.projects.viewByPhase') },
      ] as const,
    [t],
  )

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-lg text-text-1">{t('portal.projects.title')}</h1>
          <p className="text-xs text-text-2">{t('portal.projects.subtitle')}</p>
        </div>

        <Segmented
          label={t('portal.projects.viewLabel')}
          value={view}
          options={views}
          onChange={(next) => setView(next)}
        />
      </header>

      {view === 'todos' ? <FlatList /> : <ByPhase />}
    </div>
  )
}

function FlatList() {
  const t = useT()
  const query = usePortalProjects()

  if (query.isPending) return <SkeletonCards count={4} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const projects = query.data ?? []

  if (projects.length === 0) {
    return (
      <EmptyState
        title={t('portal.projects.emptyTitle')}
        description={t('portal.projects.emptyDescription')}
      />
    )
  }

  const ongoing = projects.filter((project) => project.is_ongoing)
  const delivered = projects.filter((project) => !project.is_ongoing)

  return (
    <div className="flex flex-col gap-5">
      <Grid label={t('common.label.projects')} projects={delivered} />

      {ongoing.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-2xs tracking-wide text-text-3 uppercase">
            {t('portal.projects.ongoingTitle')}
          </h2>
          <p className="max-w-[60ch] text-xs text-text-2">
            {t('portal.projects.ongoingDescription')}
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {ongoing.map((project) => (
              <PortalProjectCard key={project.id} project={project} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function ByPhase() {
  const t = useT()
  const query = usePortalBoard()

  if (query.isPending) return <SkeletonCards count={4} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const board = query.data
  const columns = (board?.columns ?? []).filter((column) => column.projects.length > 0)

  if (columns.length === 0 && (board?.ongoing.length ?? 0) === 0) {
    return (
      <EmptyState
        title={t('portal.projects.emptyTitle')}
        description={t('portal.projects.emptyDescription')}
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {columns.map((column) => (
        <section key={column.stage.label} className="flex flex-col gap-2">
          <h2 className="flex items-center gap-2">
            <PhaseBadge label={column.stage.label} />
            <span className="num text-2xs text-text-3">{column.projects.length}</span>
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {column.projects.map((project) => (
              <PortalProjectCard key={project.id} project={project} />
            ))}
          </div>
        </section>
      ))}

      {board && board.ongoing.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-2xs tracking-wide text-text-3 uppercase">
            {t('portal.projects.ongoingTitle')}
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {board.ongoing.map((project) => (
              <PortalProjectCard key={project.id} project={project} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function Grid({ label, projects }: { label: string; projects: PortalProjectListItem[] }) {
  if (projects.length === 0) return null

  return (
    <section className="flex flex-col gap-2" aria-label={label}>
      <div className="grid gap-3 md:grid-cols-2">
        {projects.map((project) => (
          <PortalProjectCard key={project.id} project={project} />
        ))}
      </div>
    </section>
  )
}
